interface CachedPDF {
  data: ArrayBuffer
  filename: string
  size: number
  uploadTime: number
  projectId: string
}

class PDFCacheService {
  private cache = new Map<string, CachedPDF>()
  private readonly MAX_CACHE_SIZE = 100 * 1024 * 1024 // 100MB total cache
  private readonly MAX_CACHE_AGE = 30 * 60 * 1000 // 30 minutes

  /**
   * Store PDF data in cache
   */
  cachePDF(fileId: string, data: ArrayBuffer, filename: string, projectId: string): void {
    // Clean expired entries before adding new one
    this.cleanExpiredEntries()
    
    // Check if we need to free up space
    this.ensureCacheSpace(data.byteLength)
    
    const cachedPDF: CachedPDF = {
      data: data.slice(), // Create a copy
      filename,
      size: data.byteLength,
      uploadTime: Date.now(),
      projectId
    }
    
    this.cache.set(fileId, cachedPDF)
    console.log(`📦 Cached PDF: ${filename} (${(data.byteLength / 1024 / 1024).toFixed(1)}MB) for project ${projectId}`)
    console.log(`📦 Cache size: ${this.cache.size} files, ${(this.getCurrentCacheSize() / 1024 / 1024).toFixed(1)}MB`)
  }

  /**
   * Get PDF data from cache
   */
  getCachedPDF(fileId: string): ArrayBuffer | null {
    const cached = this.cache.get(fileId)
    
    if (!cached) {
      return null
    }
    
    // Check if expired
    if (Date.now() - cached.uploadTime > this.MAX_CACHE_AGE) {
      this.cache.delete(fileId)
      console.log(`🗑️ Removed expired PDF from cache: ${cached.filename}`)
      return null
    }
    
    console.log(`✅ Retrieved PDF from cache: ${cached.filename} (${(cached.size / 1024 / 1024).toFixed(1)}MB)`)
    return cached.data.slice() // Return a copy
  }

  /**
   * Check if PDF is cached
   */
  hasCachedPDF(fileId: string): boolean {
    return this.getCachedPDF(fileId) !== null
  }

  /**
   * Remove PDF from cache
   */
  removePDF(fileId: string): void {
    const cached = this.cache.get(fileId)
    if (cached) {
      this.cache.delete(fileId)
      console.log(`🗑️ Removed PDF from cache: ${cached.filename}`)
    }
  }

  /**
   * Clear all cached PDFs
   */
  clearCache(): void {
    this.cache.clear()
    console.log('🗑️ Cleared PDF cache')
  }

  /**
   * Get current cache size in bytes
   */
  private getCurrentCacheSize(): number {
    let totalSize = 0
    for (const cached of this.cache.values()) {
      totalSize += cached.size
    }
    return totalSize
  }

  /**
   * Clean expired cache entries
   */
  private cleanExpiredEntries(): void {
    const now = Date.now()
    const expiredKeys: string[] = []
    
    for (const [fileId, cached] of this.cache.entries()) {
      if (now - cached.uploadTime > this.MAX_CACHE_AGE) {
        expiredKeys.push(fileId)
      }
    }
    
    for (const key of expiredKeys) {
      const cached = this.cache.get(key)
      this.cache.delete(key)
      console.log(`🗑️ Removed expired PDF from cache: ${cached?.filename}`)
    }
  }

  /**
   * Ensure we have enough space for new PDF
   */
  private ensureCacheSpace(newFileSize: number): void {
    // If new file is larger than max cache size, don't cache it
    if (newFileSize > this.MAX_CACHE_SIZE) {
      console.log(`⚠️ PDF too large to cache: ${(newFileSize / 1024 / 1024).toFixed(1)}MB`)
      return
    }
    
    // Remove oldest entries until we have enough space
    while (this.getCurrentCacheSize() + newFileSize > this.MAX_CACHE_SIZE && this.cache.size > 0) {
      // Find oldest entry
      let oldestKey = ''
      let oldestTime = Date.now()
      
      for (const [fileId, cached] of this.cache.entries()) {
        if (cached.uploadTime < oldestTime) {
          oldestTime = cached.uploadTime
          oldestKey = fileId
        }
      }
      
      if (oldestKey) {
        const cached = this.cache.get(oldestKey)
        this.cache.delete(oldestKey)
        console.log(`🗑️ Removed old PDF to free space: ${cached?.filename}`)
      }
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { fileCount: number; totalSize: number; maxSize: number } {
    return {
      fileCount: this.cache.size,
      totalSize: this.getCurrentCacheSize(),
      maxSize: this.MAX_CACHE_SIZE
    }
  }
}

// Export singleton instance
export const pdfCache = new PDFCacheService() 